import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { Router } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgxSpinnerService } from 'ngx-spinner';
import { CommonPaginationComponent } from '../common-component-pagination/common-component-pagination.component';
import { CommonApiService } from '../CommonController/common-api-service';
import { ProductionDeviceApiService } from '../CommonController/production-device-api-service';
import { exportAsCsvFile } from '../shared/function/exportDataToCsv';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-production-device',
  templateUrl: './production-device.component.html',
  styleUrls: ['./production-device.component.scss'],
  standalone: true,
  imports: [
    MatIcon,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    CommonPaginationComponent,
    NgxPaginationModule,
  ],
})
export class ProductionDeviceComponent implements OnInit {
  addProductionDeviceForm: FormGroup;

  fileToUpload: File | null = null; // Store the file outside the form
  fileUploaded = false;

  userPermission = [];
  loggedId = '';

  isUploadBoxVisible = true;

  exportData = [];

  productionUploadedDeviceList = [];
  productionUploadedDevicePage = 1;
  productionUploadedDeviceSize = 10;

  productionDuplicateDeviceList = [];
  productionDuplicateDevicePage = 1;
  productionDuplicateDeviceSize = 10;

  productionInvalidDeviceList = [];
  productionInvalidDevicePage = 1;
  productionInvalidDeviceSize = 10;

  projectName = environment.projectName;

  constructor(
    public productionDeviceApiService: ProductionDeviceApiService,
    public commonApiService: CommonApiService,
    private fb: FormBuilder,
    public router: Router,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.checkPermission();
    this.isUploadBoxVisible = true;
    this.setProductionDeviceForm();
    this.onFileChange('');
  }

  checkPermission() {
    this.userPermission = JSON.parse(sessionStorage.getItem('userPermissions'));
    this.loggedId = sessionStorage.getItem('loggedInUserId');
    this.userPermission.forEach((element) => {
      if (
        element.pageName === 'Production Device' &&
        element.create === false
      ) {
        this.navigateBack();
      }
    });
  }

  setProductionDeviceForm() {
    this.addProductionDeviceForm = this.fb.group({
      productionDetails: new FormControl('', [Validators.required]),
      file: new FormControl('', [Validators.required]),
    });
  }

  onFileChange(ev) {
    try {
      if (ev.target.files && ev.target.files.length > 0) {
        const file = ev.target.files[0];

        if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
          // Store file in variable (not the form)
          this.addProductionDeviceForm.get('file').setValue(file.name);
          this.fileToUpload = file;
          this.fileUploaded = true;
        } else {
          this.commonApiService.openSnackbar(
            'Please upload a valid Excel file'
          );
        }
      } else {
        this.fileToUpload = null;
        this.fileUploaded = false;
        this.addProductionDeviceForm.get('file').setValue('');
      }
    } catch (error) {
      this.commonApiService.openSnackbar(
        error.error.message || 'Please upload a valid Excel file'
      );
      this.addProductionDeviceForm.get('file').setValue('');
      this.fileToUpload = null;
      this.fileUploaded = false;
    }
  }

  saveDeviceDetails() {
    // Clear existing lists before starting a new upload
    this.productionUploadedDeviceList = [];
    this.productionDuplicateDeviceList = [];
    this.productionInvalidDeviceList = [];

    // Create FormData for file upload
    const formData = new FormData();
    if (this.fileToUpload) {
      formData.append('file', this.fileToUpload); // Attach the file
    } else {
      this.commonApiService.openSnackbar(
        'Please upload file before submitting.'
      );
    }

    // Retrieve userId from session storage
    const userId = this.loggedId;
    if (userId) {
      formData.append('userId', userId); // Append userId dynamically
    } else {
      this.commonApiService.openSnackbar(
        'User ID not found. Please log in again.'
      );
      return; // Stop execution if userId is missing
    }

    // Attach form values (if any) to the FormData
    const payload = this.addProductionDeviceForm.value;
    for (const key in payload) {
      if (payload[key] && key !== 'file') {
        formData.append(key, payload[key]);
      }
    }

    this.spinner.show();

    this.productionDeviceApiService
      .saveProductionDataUsingExcel(formData)
      .subscribe({
        next: (res) => {
          this.spinner.hide();

          if (res.success) {
            // Check success using 'success' property
            this.isUploadBoxVisible = false;
            this.addProductionDeviceForm.reset();
            this.commonApiService.openSnackbar(res.message);

            // Map the successfully uploaded devices from `data`
            if (res.data.savedDevices && res.data.savedDevices.length > 0) {
              this.productionUploadedDeviceList = res.data.savedDevices;
            }

            // Map the error messages for invalid devices
            if (
              res.data.duplicateDevices &&
              res.data.duplicateDevices.length > 0
            ) {
              this.productionDuplicateDeviceList = res.data.duplicateDevices;
            }

            // Map the error messages for invalid devices
            if (res.data.invalidDevices && res.data.invalidDevices.length > 0) {
              this.productionInvalidDeviceList = res.data.invalidDevices;
            }

            this.commonApiService.openSnackbar(res.message);
          } else {
            this.commonApiService.openSnackbar(
              'Failed to upload production data'
            );
          }
        },
        error: (error) => {
          this.spinner.hide();
          this.commonApiService.openSnackbar(
            error.error.message ||
              'An error occurred while uploading production data'
          );
        },
      });
  }

  onPageChangeproductionInvalidDeviceList(event) {
    this.productionInvalidDevicePage = event;
  }

  onSizeChangeproductionInvalidDeviceList(newPageSize) {
    this.productionInvalidDeviceSize = newPageSize;
    this.productionInvalidDevicePage = 1;
  }

  onPageChangeproductionDuplicateDeviceList(event) {
    this.productionDuplicateDevicePage = event;
  }

  onSizeChangeproductionDuplicateDeviceList(newPageSize) {
    this.productionDuplicateDeviceSize = newPageSize;
    this.productionDuplicateDevicePage = 1;
  }

  onPageChangeproductionUploadedDeviceList(event) {
    this.productionUploadedDevicePage = event;
  }

  onSizeChangeproductionUploadedDeviceList(newPageSize) {
    this.productionUploadedDeviceSize = newPageSize;
    this.productionUploadedDevicePage = 1;
  }

  downloadMyFile() {
    const isSampark = this.projectName === 'Sampark';
    const fileName = isSampark
      ? 'Sampark_Sample_Production_Sheet.xlsx'
      : 'Sample_Production_Sheet.xlsx';

    const link = document.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute('href', `../../assets/${fileName}`);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  exportToExcelDuplicateProducedDeviceData(productionDuplicateDeviceList) {
    if (confirm('Are you sure?')) {
      this.exportData = productionDuplicateDeviceList;
      exportAsCsvFile(this.exportData, 'Duplicate_Produced_Device_Details_');
      this.commonApiService.openSnackbar('File Ready For Download.');
    }
  }

  exportToExcelInvalidProducedDeviceData(productionInvalidDeviceList) {
    if (confirm('Are you sure?')) {
      this.exportData = productionInvalidDeviceList;
      exportAsCsvFile(this.exportData, 'Invalid_Produced_Device_Details_');
      this.commonApiService.openSnackbar('File Ready For Download.');
    }
  }

  exportToExcelUploadedProducedDeviceData(productionUploadedDeviceList) {
    if (confirm('Are you sure?')) {
      this.exportData = productionUploadedDeviceList;
      exportAsCsvFile(this.exportData, 'Uploaded_Produced_Device_Details_');
      this.commonApiService.openSnackbar('File Ready For Download.');
    }
  }

  redirect(to): void {
    this.router.navigate([to]);
  }

  navigateBack() {
    this.commonApiService.back();
  }
}
